import { useModal } from '@/components/modal/useModal';
import { Action } from '@/components/sections/Card';
import SiEdit from '@/icons/SiEdit';
import { BaseMargin, Price, Product } from '@/services/api/contracts/catalog/types';
import { FC, useRef } from 'react';
import { ProductCard } from '../../../../../components/catalogue/products/cards/ProductCard';
import { EditProductInformationModal } from '../EditProductInformationModal';

import { useVirtualizer } from '@tanstack/react-virtual';
import { z } from 'zod';

interface ProductReviewGridProps {
  products: ExtendedBulkProduct[];
  editProduct: (recipe: (products: ExtendedBulkProduct[]) => void) => void;
}

export const ProductReviewGrid: FC<ProductReviewGridProps> = ({ products, editProduct }) => {
  const editProductInformationModalProps = useModal<{ productId: string }>();

  const rows = products.reduce((acc, product, index) => {
    const rowIndex = Math.floor(index / 3);
    const columnIndex = index % 3;
    if (!acc[rowIndex]) {
      acc[rowIndex] = [];
    }
    acc[rowIndex][columnIndex] = product;
    return acc;
  }, [] as ExtendedBulkProduct[][]);

  const parentRef = useRef<HTMLDivElement | null>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 150
  });

  const priceHistoryModalProps = useModal<{
    productId: string;
  }>();

  return (
    <>
      <EditProductInformationModal
        {...editProductInformationModalProps}
        products={products}
        editProduct={editProduct}
      />
      <div ref={parentRef} className="h-[32rem] w-full overflow-y-scroll">
        <div
          style={{
            height: virtualizer.getTotalSize(),
            width: '100%',
            position: 'relative'
          }}
        >
          {virtualizer.getVirtualItems().map(column => (
            <div
              className="grid grid-cols-3 gap-4 pb-6"
              key={column.key}
              data-index={column.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${column.start}px)`
              }}
            >
              {rows[column.index].map((product, index) => (
                <ProductCard
                  key={`${product.productId}-${index}`}
                  product={{
                    id: product.productId,
                    code: '',
                    price: {
                      price: product.catalogPrice.price,
                      date: product.catalogPrice.effectiveDate.value,
                      margins: [
                        {
                          party: '',
                          margin: product.baseMargin.modifier,
                          marginType: 0,
                          contribution: 0
                        }
                      ]
                    },
                    ...product
                  }}
                  priceHistoryModalProps={priceHistoryModalProps}
                >
                  <Action
                    onClick={() => editProductInformationModalProps.open({ productId: product.productId })}
                    className="flex w-full items-center justify-center gap-1 p-2 text-sm"
                  >
                    <SiEdit className="text-2xl" />
                    <span className="font-medium">Edit product information</span>
                  </Action>
                </ProductCard>
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export const ExtendedBulkProduct = Product.omit({ id: true, price: true, code: true }).extend({
  catalogPrice: Price,
  baseMargin: BaseMargin
});

export type ExtendedBulkProduct = z.infer<typeof ExtendedBulkProduct>;
